export type CustomOfferCheckoutPath = 'custom-offer' | 'custom-offer-v2' | 'payment-schedule-balance'
export type CustomOfferV2Type = 'full_payment' | 'payment_schedule' | 'manual' | undefined
export type CustomOfferPaymentSummaryRow = {
  kind: 'deposit_due' | 'deposit_paid' | 'first_payment_due' | 'payment_due' | 'balance_due'
  date?: string
  amount: number
}

function getCustomOfferPaymentMetadata(order?: App.Order): App.CustomOfferPaymentMetadata | undefined {
  return order?.customOfferItems?.[0]?.custom_offer?.payment_metadata
}

function getV1CustomOfferPaymentSummaryRows(order: App.Order): Array<CustomOfferPaymentSummaryRow> {
  const customOffer = order.customOfferItems?.[0]?.custom_offer
  const paymentMetadata = customOffer?.payment_metadata
  const rows: Array<CustomOfferPaymentSummaryRow> = []

  if (paymentMetadata?.payment_type === 'deposit' && paymentMetadata.customer_deposit_1_amount != null) {
    rows.push({
      kind: paymentMetadata.customer_deposit_payment_made ? 'deposit_paid' : 'deposit_due',
      ...(!paymentMetadata.customer_deposit_payment_made && { date: paymentMetadata.customer_deposit_1_due_date }),
      amount: paymentMetadata.customer_deposit_1_amount / 100,
    })
  }

  rows.push({
    kind: 'balance_due',
    date: customOffer?.due_date,
    amount: Number(order.total ?? 0),
  })

  return rows
}

function getV2CustomOfferPaymentSummaryRows(order: App.Order): Array<CustomOfferPaymentSummaryRow> {
  const paymentFlowV2 = getCustomOfferPaymentMetadata(order)?.payment_flow_v2

  switch (paymentFlowV2?.type) {
    case 'full_payment':
      return [{
        kind: 'payment_due',
        date: paymentFlowV2.payment_deadline,
        amount: Number(order.total ?? 0),
      }]
    case 'payment_schedule':
      return [{
        kind: 'first_payment_due',
        date: paymentFlowV2.payment_deadline,
        amount: Number(paymentFlowV2.deposit_amount ?? 0),
      }]
    case 'manual':
      return [
        {
          kind: paymentFlowV2.manual_deposit_payment_made ? 'deposit_paid' : 'deposit_due',
          ...(!paymentFlowV2.manual_deposit_payment_made && { date: paymentFlowV2.manual_deposit_due_date }),
          amount: Number(paymentFlowV2.manual_deposit_amount ?? 0),
        },
        {
          kind: 'balance_due',
          date: paymentFlowV2.manual_balance_due_date,
          amount: Number(paymentFlowV2.manual_balance_amount ?? 0),
        },
      ]
    default:
      return []
  }
}

export function getCustomOfferPaymentSummaryRows(order: App.Order): Array<CustomOfferPaymentSummaryRow> {
  const paymentMetadata = getCustomOfferPaymentMetadata(order)

  if (paymentMetadata?.payment_flow_version === 'v2') {
    return getV2CustomOfferPaymentSummaryRows(order)
  }

  return getV1CustomOfferPaymentSummaryRows(order)
}

export function getCustomOfferCheckoutPath(order?: App.Order): CustomOfferCheckoutPath {
  const paymentMetadata = getCustomOfferPaymentMetadata(order)
  if (paymentMetadata?.payment_flow_version !== 'v2') {
    return 'custom-offer'
  }

  if (
    paymentMetadata.payment_flow_v2?.type === 'payment_schedule' &&
    order?.paymentScheduleDetails?.charges.some(charge => charge.status === 'paid') &&
    order?.paymentScheduleDetails?.charges.some(charge => charge.status === 'pending' || charge.status === 'failed')
  ) {
    return 'payment-schedule-balance'
  }

  return 'custom-offer-v2'
}

export function getCustomOfferV2Type(order?: App.Order): CustomOfferV2Type {
  const paymentMetadata = getCustomOfferPaymentMetadata(order)

  if (paymentMetadata?.payment_flow_version !== 'v2') {
    return
  }

  return paymentMetadata.payment_flow_v2?.type
}

export function getCustomOfferDueDate(order?: App.Order): string | undefined {
  const customOffer = order?.customOfferItems?.[0]?.custom_offer
  const paymentMetadata = customOffer?.payment_metadata

  if (paymentMetadata?.payment_flow_version !== 'v2') {
    return customOffer?.due_date
  }

  const v2 = paymentMetadata.payment_flow_v2
  switch (v2?.type) {
    case 'full_payment':
    case 'payment_schedule':
      return v2.payment_deadline
    case 'manual':
      return v2.manual_balance_due_date
    default:
      return undefined
  }
}

export function buildCustomOfferV2PaymentMetadata(order: App.Order): App.PaymentPlan['payment_metadata'] | undefined {
  const paymentMetadata = getCustomOfferPaymentMetadata(order)
  const v2 = paymentMetadata?.payment_flow_v2

  if (paymentMetadata?.payment_flow_version !== 'v2' || !v2?.type) {
    return undefined
  }

  return {
    payment_flow_version: 'v2',
    payment_flow_v2: {
      type: v2.type,
      deposit_amount: v2.deposit_amount,
      balance_amount: v2.balance_amount,
      balance_due_date: v2.balance_due_date,
    },
  }
}

export function isInitialSalesCustomOfferPurchase(order: App.Order, postPurchaseMode?: string): boolean {
  const customOffer = order.customOfferItems?.[0]?.custom_offer
  if (customOffer?.purchase_type !== 'sales') {
    return false
  }

  const paymentFlowType = getCustomOfferV2Type(order)
  if (paymentFlowType === 'full_payment') {
    return true
  }

  return paymentFlowType === 'payment_schedule' &&
    (postPurchaseMode === 'custom-offer' || postPurchaseMode === 'custom-offer-v2')
}
